import { ReadonlyURLSearchParams } from "next/navigation";
import { logClientError } from "@repo/next-utils/newrelic-client-utils";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";
import { getSierraConfig } from "./get-sierra-config";
import { getSierraInstance } from "./get-sierra-instance";

export const setChatWidgetParam = () => {
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.searchParams.set("showChatWidget", "true");
    window.history.replaceState({}, "", url.toString());
  }
};

export const deleteChatWidgetParam = () => {
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    url.searchParams.delete("showChatWidget");
    window.history.replaceState({}, "", url.toString());
  }
};

type SierraChatTimeoutActionParams = {
  searchParams: ReadonlyURLSearchParams | null;
  setInitError: (error: Error | null) => void;
  setShowSierraChat: (show: boolean) => void;
};

export const sierraChatTimeoutAction = ({
  searchParams,
  setInitError,
  setShowSierraChat,
}: SierraChatTimeoutActionParams) => {
  const timeout = setTimeout(() => {
    const hasOpenChatParam =
      searchParams?.has("showChatWidget") ||
      new URLSearchParams(window.location.search).has("showChatWidget");

    const sierraChatContainer = document.querySelector(
      "#sierra-chat-container",
    );

    const sierraChatWidget =
      sierraChatContainer?.shadowRoot?.querySelector("main");

    const sierraEmbedScript = document.querySelector<HTMLScriptElement>(
      'script[type="module"][src*="/agent/"][src$="/embed"]',
    );
    const isSierraScriptStillLoading =
      !!sierraEmbedScript && sierraEmbedScript.dataset.loaded !== "true";

    if (!sierraChatWidget && hasOpenChatParam) {
      if (window.sierra) {
        window.sierra.openChatModal();
        return;
      }

      if (isSierraScriptStillLoading) {
        return;
      }

      deleteChatWidgetParam();
      const timeoutError = new Error("Sierra Chat: Timeout error");
      logClientError({ error: timeoutError });
      setShowSierraChat(false);
      setInitError(timeoutError);
    }
  }, 5000);

  return timeout;
};

type InitParams = {
  auth: ReturnType<typeof useAuth>;
  searchParams: ReadonlyURLSearchParams | null;
  store: ReturnType<typeof useStore>;
  setShowSierraChat: (show: boolean) => void;
  toggleChatModal: () => void;
};

export const init = async ({
  auth,
  searchParams,
  store,
  setShowSierraChat,
  toggleChatModal,
}: InitParams) => {
  const sierraInstance = getSierraInstance({ searchParams });

  window.sierraConfig = await getSierraConfig({
    auth,
    searchParams,
    store,
  });

  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[type="module"][src="${sierraInstance}"]`,
  );

  if (existingScript) {
    const shouldAutoOpen = searchParams?.get("showChatWidget") === "true";

    if (shouldAutoOpen) {
      if (window.sierra) {
        toggleChatModal();
      } else if (existingScript.dataset.loaded === "true") {
        let attempts = 0;
        const maxAttempts = 50;
        const interval = window.setInterval(() => {
          attempts += 1;

          if (window.sierra) {
            window.clearInterval(interval);
            toggleChatModal();
            return;
          }

          if (attempts >= maxAttempts) {
            window.clearInterval(interval);
          }
        }, 100);

        return () => {
          window.clearInterval(interval);
        };
      } else {
        const onLoad = () => {
          existingScript.dataset.loaded = "true";
          toggleChatModal();
        };

        existingScript.addEventListener("load", onLoad, { once: true });

        return () => {
          existingScript.removeEventListener("load", onLoad);
        };
      }
    }

    return () => {};
  }

  const agentScript = document.createElement("script");
  agentScript.type = "module";
  agentScript.onload = () => {
    agentScript.dataset.loaded = "true";
    if (searchParams?.get("showChatWidget") === "true") {
      toggleChatModal();
    }
  };
  agentScript.onerror = () => {
    const error = new Error("Sierra Chat: Script load error.");
    logClientError({ error });
    setShowSierraChat(false);
  };
  agentScript.src = sierraInstance;
  document.head.appendChild(agentScript);

  return () => {
    document.head.removeChild(agentScript);
  };
};
